import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import { COLLECTING_REQUEST_STATUS_BADGE_COLOR, DATE_FORMAT } from '~/constants';
import { COLLECTING_SHIPPING_LABEL_NUMBER_TOOLTIP } from '~/constants/delivery-agency';

import { StatusBadge, joinOrDash } from './deliveryAgencyTableCells';

import type { CollectingRequestListItem } from '~/types/delivery-agency.types';

const getDeliveryAgencyCollectingRequestsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (collectingRequest: CollectingRequestListItem) => void;
}) => {
  const columnDefs: TypeTableColumn<CollectingRequestListItem>[] = [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => row.index + 1 + currentPage * pageSize,
      size: 88,
      meta: { selection: true },
    },
    {
      accessorKey: 'collectingRequestedDate',
      header: '반품접수일',
      size: 110,
      cell: ({ row }) => formatDate(row.original.collectingRequestedDate, DATE_FORMAT.DATE) ?? '-',
    },
    {
      accessorKey: 'collectingRequestStatus',
      header: '회수 상태',
      size: 120,
      cell: ({ row }) => {
        const value = row.original.collectingRequestStatus;
        if (!value) return '-';

        return <StatusBadge value={value} colors={COLLECTING_REQUEST_STATUS_BADGE_COLOR} />;
      },
    },
    {
      accessorKey: 'statusUpdatedAt',
      header: '상태 마지막 업데이트 일시',
      size: 190,
      cell: ({ row }) => formatDate(row.original.statusUpdatedAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      accessorKey: 'collectingRequestCode',
      header: '반품요청번호',
      size: 160,
      cell: ({ getValue, row }) => (
        <TextButtonCell value={getValue()} onClick={() => onClickViewDetail(row.original)}>
          {row.original.collectingRequestCode ?? '-'}
        </TextButtonCell>
      ),
    },
    {
      accessorKey: 'collectingShippingLabelNumbers',
      header: '회수 운송장번호',
      meta: { headerTooltip: { description: COLLECTING_SHIPPING_LABEL_NUMBER_TOOLTIP } },
      size: 180,
      cell: ({ row }) => <TooltipTextCell valueFormatted={joinOrDash(row.original.collectingShippingLabelNumbers)} />,
    },
    {
      accessorKey: 'productName',
      header: '회수할 상품명',
      size: 180,
      cell: ({ getValue }) => <TooltipTextCell value={getValue()} />,
    },
    {
      accessorKey: 'collectedProductHandling',
      header: '회수한 상품 처리 방식',
      size: 160,
      cell: ({ row }) => row.original.collectedProductHandling?.description || '-',
    },
    {
      accessorKey: 'contactName',
      header: '원주문 수령자명',
      size: 120,
    },
    {
      accessorKey: 'originalOutboundCode',
      header: '출고번호',
      size: 160,
    },
    {
      accessorKey: 'clientOrderCodes',
      header: '판매처 주문번호(원주문)',
      minSize: 190,
      cell: ({ row }) => <TooltipTextCell valueFormatted={joinOrDash(row.original.clientOrderCodes)} />,
      meta: { flex: true },
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyCollectingRequestsColumnDefs;
